import React from 'react';
import { Typography } from 'antd';
import { PhoneOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { hasAnyValue } from '../../../types/clientProfile';

interface Props {
  phone: string | null;
  telegramUsername: string | null;
}

interface InfoRowProps {
  icon: React.ReactNode;
  iconColor: string;
  iconBg: string;
  label: string;
  value: string | null;
  placeholder?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({ icon, iconColor, iconBg, label, value, placeholder }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    <div style={{
      width: 36, height: 36, borderRadius: 'var(--bl-radius-sm)', flexShrink: 0,
      background: iconBg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 16, color: iconColor,
    }}>
      {icon}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <Typography.Text style={{ display: 'block', fontSize: 11, color: 'var(--bl-text-secondary)', fontWeight: 500 }}>
        {label}
      </Typography.Text>
      <Typography.Text style={{ fontSize: 15, fontWeight: 600, color: value ? 'var(--bl-text)' : 'var(--bl-text-secondary)' }}>
        {value ?? placeholder ?? '—'}
      </Typography.Text>
    </div>
  </div>
);

export const AccountCard: React.FC<Props> = ({ phone, telegramUsername }) => {
  const { t } = useTranslation('customer');
  if (!hasAnyValue({ phone, telegramUsername })) return null;
  return (
    <div style={{
      margin: '12px 16px 0',
      borderRadius: 'var(--bl-radius-lg)',
      background: 'var(--bl-surface)',
      border: '1px solid var(--bl-divider)',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '12px 16px 10px', borderBottom: '1px solid var(--bl-divider)' }}>
        <Typography.Text style={{ fontSize: 13, fontWeight: 700, color: 'var(--bl-text)' }}>
          {t('profile.account.title')}
        </Typography.Text>
      </div>
      {/* Rows */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        <InfoRow
          icon={<PhoneOutlined />}
          iconColor="rgba(31,139,76,1)"
          iconBg="rgba(31,139,76,0.10)"
          label={t('profile.account.phone')}
          value={phone}
          placeholder={t('profile.account.phonePlaceholder')}
        />
        {telegramUsername !== null && (
          <InfoRow
            icon={<SendOutlined />}
            iconColor="var(--bl-primary)"
            iconBg="rgba(51,144,236,0.10)"
            label={t('profile.account.telegramUsername')}
            value={`@${telegramUsername.replace(/^@/, '')}`}
          />
        )}
      </div>
    </div>
  );
};
